import React, { useEffect, useState } from 'react'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core'
import { getFeaturesConfigApps, getAppFeaturesConfig, syncS3AppFeaturesConfig } from '../../../../_metronic/helpers/backend_helper'
import { AppList } from '../components'
import { ConfirmationDialog } from '../../../Components/UI'
import { detectChanges } from '../../../helpers/utils/changeDetection'
import './UpdateFeatures.scss';



const UpdateFeatures: React.FC = () => {
  const [apps, setApps] = useState<[]>([])
  const [appConfig, setAppConfig] = useState<object>({})
  const [platforms, setPlatforms] = useState<string[]>([])
  const [selectedAppName, setSelectedAppName] = useState<string>('')
  const [selectedAppToken, setSelectedAppToken] = useState<string>('')
  const [selectedPlatform, setSelectedPlatform] = useState<string>('')
  const [selectedAppData, setSelectedAppData] = useState<object>({})
  const [selectedPlatformData, setSelectedPlatformData] = useState<object>({})
  const [formData, setFormData] = useState<object>({})

  const [isLoadingApps, setIsLoadingApps] = useState<boolean>(true)
  const [isLoadingAppData, setIsLoadingAppData] = useState<boolean>(false)

  // Add state for tracking form data for all platforms
  const [allPlatformsFormData, setAllPlatformsFormData] = useState<{ [platform: string]: object }>({})

  // Add state for tracking deleted items for all platforms
  const [allPlatformsDeletedItems, setAllPlatformsDeletedItems] = useState<{ [platform: string]: { [key: string]: string[] | 'DELETED_FEATURE' } }>({})

  // Add state for tracking deleted items (current platform)
  const [currentPlatformDeletedItems, setcurrentPlatformDeletedItems] = useState<{ [key: string]: string[] | 'DELETED_FEATURE' }>({})

  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false)
  const [isConfirming, setIsConfirming] = useState<boolean>(false)
  const [collapsedCards, setCollapsedCards] = useState<Set<string>>(new Set([]))


  // On component mount, send http get request to get the apps and platforms
  useEffect(() => {
    const fetchApps = async () => {
      setIsLoadingApps(true)
      try {
        const data = await getFeaturesConfigApps();
        setApps(data);
      } catch (error: any) {
        console.error('Error fetching apps:', error);
        if (error.message && error.message.includes('401')) {
          // Handle unauthorized error, e.g., redirect to login or show a message
        }
      } finally {
        setIsLoadingApps(false)
      }
    };
    fetchApps();
  }, []);

  // Auto-select first platform when app data loads
  useEffect(() => {
    if (platforms.length > 0 && selectedAppData && !selectedPlatform && !isLoadingAppData) {
      // Initialize all platforms form data with original data
      const initialFormData: { [platform: string]: object } = {};
      const initialDeletedItems: { [platform: string]: { [key: string]: string[] | 'DELETED_FEATURE' } } = {};

      platforms.forEach(platform => {
        const platformData = selectedAppData[platform] || {};
        initialFormData[platform] = JSON.parse(JSON.stringify(platformData));
        initialDeletedItems[platform] = {};
      });

      setAllPlatformsFormData(initialFormData);
      setAllPlatformsDeletedItems(initialDeletedItems);

      // Directly set the first platform without going through onPlatformSelection
      setSelectedPlatform(platforms[0]);
      const platformData = selectedAppData[platforms[0]] || {};
      setSelectedPlatformData(JSON.parse(JSON.stringify(platformData))); // Deep copy for original data
      setFormData(JSON.parse(JSON.stringify(platformData))); // Deep copy for form data
      setCollapsedCards(new Set(Object.keys(platformData)))
    } else if (platforms.length === 0) {
      // Clear selected platform when no platforms exist
      setSelectedPlatform('');
      setSelectedPlatformData({});
      setFormData({});
      setAllPlatformsFormData({});
      setAllPlatformsDeletedItems({});
    }
  }, [selectedAppData, platforms, selectedPlatform, isLoadingAppData]);

  const configBreadcrumbs: Array<PageLink> = [
    {
      title: 'Home /',
      path: '/dashboard/apps',
      isSeparator: false,
      isActive: false,
    },
  ]

  const onAppSelection = async (app_token: string, app_name: string) => {
    console.log('onAppSelection', app_token, app_name)
    // Set the selected app immediately so the loading indicator section is rendered
    setSelectedAppName(app_name);
    setSelectedAppToken(app_token);

    // Reset platform selection when selecting a new app
    setSelectedPlatform('');
    setSelectedPlatformData({});
    setFormData({});
    setcurrentPlatformDeletedItems({});
    setAllPlatformsFormData({});
    setAllPlatformsDeletedItems({});

    const fetchAppConfigs = async (app_token: string, app_name: string) => {
      try {
        const data = await getAppFeaturesConfig(app_token, app_name);
        return data;
      } catch (error: any) {
        // Handle 404 case - return empty object if no data found
        if (error.status === 404) {
          return {};
        }
        console.error('Error fetching app configs:', error);
        throw error;
      }
    };

    setIsLoadingAppData(true)
    try {
      // Load the app json file from the rawData, file is stored in s3 bucket and then extract the platforms from json file
      const appData = apps.find((a: any) => a.app_name == app_name && a.app_token == app_token) as any;
      if (!appData) {
        throw new Error('App not found');
      }
      const appConfigResponse = await fetch(appData['app_config_file_path']);
      const appConfigJson = await appConfigResponse.json();
      const firstKey = Object.keys(appConfigJson)[0]; // first key is app_name
      const platforms = Object.keys(appConfigJson[firstKey]); // platforms are keys inside app_name
      const appPlatformsData = appConfigJson[firstKey];
      // loop over appPlatformsData and remove the nested community_detection field
      for (const platform of platforms) {
        if (appPlatformsData[platform] && typeof appPlatformsData[platform] === 'object' && 'community_detection' in appPlatformsData[platform]) {
          delete appPlatformsData[platform]['community_detection'];
        }
      }
      const appConfig = await fetchAppConfigs(app_token, app_name);
      const featuresConfig = appConfig?.features_config || {};
      const schemaPlatforms = Object.keys(featuresConfig);
      setAppConfig(featuresConfig);
      setSelectedAppData(appPlatformsData);
      setPlatforms(schemaPlatforms);
    } catch (error) {
      console.error('Error loading app data:', error);
    } finally {
      setIsLoadingAppData(false)
    }
  }


  const onPlatformSelection = (platform: string) => {
    // Save current platform's changes before switching
    if (selectedPlatform) {
      setAllPlatformsFormData(prev => ({
        ...prev,
        [selectedPlatform]: { ...formData }
      }));
      setAllPlatformsDeletedItems(prev => ({
        ...prev,
        [selectedPlatform]: { ...currentPlatformDeletedItems }
      }));
    }

    setSelectedPlatform(platform);
    const platformData = selectedAppData[platform] || {};
    setSelectedPlatformData(JSON.parse(JSON.stringify(platformData))); // Deep copy for original data

    // Load saved form data for this platform, or use original data if none saved
    const savedFormData = allPlatformsFormData[platform];
    const savedDeletedItems = allPlatformsDeletedItems[platform];

    setFormData(savedFormData ? JSON.parse(JSON.stringify(savedFormData)) : JSON.parse(JSON.stringify(platformData)));
    setcurrentPlatformDeletedItems(savedDeletedItems ? { ...savedDeletedItems } : {});
  }

  // Delete a parameter from a feature
  const deleteParameter = (featureKey: string, paramKey: string) => {
    // Remove from form data
    const updatedData = { ...formData };
    if (updatedData[featureKey]?.params) {
      delete updatedData[featureKey].params[paramKey];
      setFormData(updatedData);
    }

    // Track deletion
    setcurrentPlatformDeletedItems(prev => {
      const newDeletedItems = { ...prev };
      if (!newDeletedItems[featureKey]) {
        newDeletedItems[featureKey] = [];
      }
      if (!newDeletedItems[featureKey].includes(paramKey)) {
        newDeletedItems[featureKey] = [...newDeletedItems[featureKey], paramKey];
      }
      return newDeletedItems;
    });
  }

  // Delete a feature from the platform
  const deleteFeature = (featureKey: string) => {
    // Remove from form data
    const updatedData = { ...formData };
    delete updatedData[featureKey];
    setFormData(updatedData);

    // Track deletion - mark feature as deleted with a special flag
    setcurrentPlatformDeletedItems(prev => {
      const newDeletedItems = { ...prev };
      newDeletedItems[featureKey] = 'DELETED_FEATURE'; // Special flag to indicate feature deletion
      return newDeletedItems;
    });
  }

  // Delete a platform (only if it has no features)
  const deletePlatform = (platform: string) => {
    const platformData = selectedAppData[platform] || {};
    const hasFeatures = Object.keys(platformData).length > 0;

    if (hasFeatures) {
      setSaveMessage({
        type: 'error',
        text: `Cannot delete platform "${platform}" because it contains features. Please delete all features first.`
      });
      setTimeout(() => setSaveMessage(null), 5000);
      return;
    }

    // Remove platform from app data
    const updatedAppData = { ...selectedAppData };
    delete updatedAppData[platform];
    setSelectedAppData(updatedAppData);

    // Update platforms list
    const updatedPlatforms = platforms.filter(p => p !== platform);
    setPlatforms(updatedPlatforms);

    // If the deleted platform was selected, select the first available platform
    if (selectedPlatform === platform) {
      if (updatedPlatforms.length > 0) {
        setSelectedPlatform(updatedPlatforms[0]);
        setSelectedPlatformData(updatedAppData[updatedPlatforms[0]] || {});
        setFormData(updatedAppData[updatedPlatforms[0]] || {});
      } else {
        setSelectedPlatform('');
        setSelectedPlatformData({});
        setFormData({});
      }
    }

    setSaveMessage({
      type: 'success',
      text: `Platform "${platform}" deleted successfully.`
    });
    setTimeout(() => setSaveMessage(null), 5000);
  }

  // Check if a platform can be deleted (has no features)
  const canDeletePlatform = (platform: string) => {
    const platformData = selectedAppData[platform] || {};
    return Object.keys(platformData).length === 0;
  }

  const onFeatureEnabledDisabled = (featureKey: string, enabled: boolean) => {
    const updatedData = { ...formData };
    if (!updatedData[featureKey]) {
      updatedData[featureKey] = { enable: false, params: {} };
    }
    updatedData[featureKey].enable = enabled;
    setFormData(updatedData);
  }

  const handleParameterChange = (featureKey: string, paramKey: string, value: any) => {
    const updatedData = { ...formData };
    if (!updatedData[featureKey]) {
      updatedData[featureKey] = { enable: false, params: {} };
    }
    if (!updatedData[featureKey].params) {
      updatedData[featureKey].params = {};
    }
    updatedData[featureKey].params[paramKey] = value;
    console.log(updatedData);
    setFormData(updatedData);
  }

  const handleSave = () => {
    setShowConfirmationDialog(true)
  }

  const handleConfirmSave = async () => {
    setIsConfirming(true)
    setSaveMessage(null)

    try {
      // Save current platform's changes before creating the final structure
      const finalAllPlatformsFormData = {
        ...allPlatformsFormData,
        [selectedPlatform]: { ...formData }
      };

      const finalAllPlatformsDeletedItems = {
        ...allPlatformsDeletedItems,
        [selectedPlatform]: { ...currentPlatformDeletedItems }
      };

      // Create the JSON structure with all platforms' changes
      const jsonStructure = {
        [selectedAppToken]: {}
      };

      // Apply changes for all platforms
      platforms.forEach(platform => {
        const platformFormData = finalAllPlatformsFormData[platform] || {};
        const platformDeletedItems = finalAllPlatformsDeletedItems[platform] || {};

        // Start with original platform data
        let finalPlatformData = { ...selectedAppData[platform] };

        // Apply form data changes
        finalPlatformData = { ...finalPlatformData, ...platformFormData };

        // Apply deletions
        Object.entries(platformDeletedItems).forEach(([featureKey, deletionType]) => {
          if (deletionType === 'DELETED_FEATURE') {
            delete finalPlatformData[featureKey];
          } else if (Array.isArray(deletionType)) {
            // Delete specific parameters
            if (finalPlatformData[featureKey]?.params) {
              deletionType.forEach(paramKey => {
                delete finalPlatformData[featureKey].params[paramKey];
              });
            }
          }
        });

        jsonStructure[selectedAppToken][platform] = finalPlatformData;
      });

      // Create the request body with both the final structure and deletion tracking
      const requestBody = {
        config_data: jsonStructure,
        deleted_items: finalAllPlatformsDeletedItems
      };

      // Send request to the sync-s3 endpoint with deletion information
      const result = await syncS3AppFeaturesConfig(selectedAppToken, selectedAppName, requestBody);

      setSaveMessage({
        type: 'success',
        text: `Configuration saved successfully for ${selectedAppName} - all platforms.`
      })

      // Clear success message after 5 seconds
      setTimeout(() => setSaveMessage(null), 5000)

      // SMART FIX: Fetch updated configuration from backend to ensure consistency
      // This handles deletions properly since the backend removes deleted items from config
      try {
        const updatedAppConfig = await getAppFeaturesConfig(selectedAppToken, selectedAppName);
        const updatedFeaturesConfig = updatedAppConfig?.features_config || {};
        
        // Update the appConfig with the latest schema from backend
        setAppConfig(updatedFeaturesConfig);
        
        // For new features that were just added, we need to update selectedAppData
        // to include them so they no longer appear as "new"
        const updatedSelectedAppData = { ...selectedAppData };
        platforms.forEach(platform => {
          const platformFormData = finalAllPlatformsFormData[platform] || {};
          const platformDeletedItems = finalAllPlatformsDeletedItems[platform] || {};

          // Only apply additions (not deletions) to local state
          // Deletions are handled by the backend and reflected in the updated config
          Object.entries(platformFormData).forEach(([featureKey, featureData]) => {
            const wasDeleted = platformDeletedItems[featureKey] === 'DELETED_FEATURE';
            if (!wasDeleted) {
              // Add new features or update existing ones
              if (!updatedSelectedAppData[platform]) {
                updatedSelectedAppData[platform] = {};
              }
              updatedSelectedAppData[platform][featureKey] = featureData;
            }
          });
        });

        // Update the state with the new data
        setSelectedAppData(updatedSelectedAppData);

        // Update selectedPlatformData for the current platform
        const currentPlatformFinalData = updatedSelectedAppData[selectedPlatform] || {};
        setSelectedPlatformData(currentPlatformFinalData);
        
      } catch (fetchError) {
        console.warn('Could not fetch updated config, but save was successful:', fetchError);
        // If we can't fetch updated config, fall back to manual state update for additions only
        const updatedSelectedAppData = { ...selectedAppData };
        platforms.forEach(platform => {
          const platformFormData = finalAllPlatformsFormData[platform] || {};
          const platformDeletedItems = finalAllPlatformsDeletedItems[platform] || {};

          // Only apply additions (not deletions) to local state
          Object.entries(platformFormData).forEach(([featureKey, featureData]) => {
            const wasDeleted = platformDeletedItems[featureKey] === 'DELETED_FEATURE';
            if (!wasDeleted) {
              if (!updatedSelectedAppData[platform]) {
                updatedSelectedAppData[platform] = {};
              }
              updatedSelectedAppData[platform][featureKey] = featureData;
            }
          });
        });

        setSelectedAppData(updatedSelectedAppData);
        const currentPlatformFinalData = updatedSelectedAppData[selectedPlatform] || {};
        setSelectedPlatformData(currentPlatformFinalData);
      }

      // Reset all platforms' tracking after successful save
      setcurrentPlatformDeletedItems({})
      setAllPlatformsFormData({});
      setAllPlatformsDeletedItems({});

      // Close the confirmation dialog
      setShowConfirmationDialog(false)

    } catch (error) {
      console.error('Error saving configuration:', error)
      setSaveMessage({
        type: 'error',
        text: 'Failed to save configuration. Please try again.'
      })
    } finally {
      setIsConfirming(false)
    }
  }

  const toggleCardCollapse = (featureKey: string) => {
    const newCollapsedCards = new Set(collapsedCards)
    if (newCollapsedCards.has(featureKey)) {
      newCollapsedCards.delete(featureKey)
    } else {
      newCollapsedCards.add(featureKey)
    }
    setCollapsedCards(newCollapsedCards)
  }

  // Helper function to check if a feature is new (exists in schema but not in app data)
  const isNewFeature = (featureKey: string) => {
    const schemaFeatures = appConfig?.[selectedPlatform] || {};
    const appFeatures = selectedPlatformData || {};

    // Check if the feature was deleted
    const isDeleted = currentPlatformDeletedItems[featureKey] === 'DELETED_FEATURE';

    return schemaFeatures[featureKey] && !appFeatures[featureKey] && !isDeleted;
  }

  // Helper function to check if a parameter is new (exists in schema but not in values)
  const isNewParameter = (featureKey: string, paramKey: string) => {
    const schemaFeatures = appConfig?.[selectedPlatform] || {};
    const schemaFeature = schemaFeatures[featureKey];

    if (!schemaFeature?.params) return false;

    const schemaParam = schemaFeature.params.find((p: any) => p.key === paramKey);
    if (!schemaParam) return false;

    // Check if the parameter was deleted
    const deletedParams = currentPlatformDeletedItems[featureKey];
    const isDeleted = Array.isArray(deletedParams) && deletedParams.includes(paramKey);

    // Check if the parameter key exists in current form data
    const currentParams = formData[featureKey]?.params || {};
    const hasCurrentParam = paramKey in currentParams;

    // Parameter is new if it exists in schema but not in current data and wasn't deleted
    return !hasCurrentParam && !isDeleted;
  }

  // Helper function to check if any parameter in a feature is new
  const hasNewParameters = (featureKey: string) => {
    const schemaFeatures = appConfig?.[selectedPlatform] || {};
    const schemaFeature = schemaFeatures[featureKey];

    if (!schemaFeature?.params) return false;

    const currentParams = formData[featureKey]?.params || {};
    const schemaParamKeys = schemaFeature.params.map((p: any) => p.key);
    const currentParamKeys = Object.keys(currentParams);
    const deletedParamKeys = Array.isArray(currentPlatformDeletedItems[featureKey]) ? currentPlatformDeletedItems[featureKey] : [];

    // Check if there are any parameters in schema that don't exist in current data and weren't deleted
    const hasNewParams = schemaParamKeys.some(key =>
      !currentParamKeys.includes(key) && !deletedParamKeys.includes(key)
    );

    return hasNewParams;
  }

  const prettifyFeatureKey = (featureKey: string): string => {
    return featureKey
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };


  const getChanges = () => {
    const allChanges: any[] = [];

    // Save current platform's changes before calculating
    const finalAllPlatformsFormData = {
      ...allPlatformsFormData,
      [selectedPlatform]: { ...formData }
    };

    const finalAllPlatformsDeletedItems = {
      ...allPlatformsDeletedItems,
      [selectedPlatform]: { ...currentPlatformDeletedItems }
    };

    platforms.forEach(platform => {
      const platformOriginalData = selectedAppData[platform] || {};
      const platformFormData = finalAllPlatformsFormData[platform] || {};
      const platformDeletedItems = finalAllPlatformsDeletedItems[platform] || {};
      const platformConfig = appConfig?.[platform];

      const platformChanges = detectChanges(
        platformOriginalData,
        platformFormData,
        platformDeletedItems,
        platformConfig
      );

      if (platformChanges.length > 0) {
        allChanges.push(...platformChanges.map(change => ({
          ...change,
          platform: platform
        })));
      }
    });

    return allChanges;
  };

  const renderParameterInput = (featureKey: string, param: any) => {
    const currentValue = formData[featureKey]?.params?.[param.key] || '';
    const isNew = isNewParameter(featureKey, param.key);

    switch (param.type) {
      case 'number':
        return (
          <div className="position-relative">
            <input
              type="number"
              className={`form-control ${isNew ? 'border-success' : ''}`}
              value={currentValue}
              onChange={(e) => handleParameterChange(featureKey, param.key, parseFloat(e.target.value) || 0)}
              min={param.min}
              max={param.max}
              step="0.01"
            />
            {isNew && (
              <div className="position-absolute top-0 end-0 mt-1 me-2">
                <i className="bi bi-star-fill text-success" title="This parameter is new and requires to be configured"></i>
              </div>
            )}
          </div>
        );
      case 'integer':
        return (
          <div className="position-relative">
            <input
              type="number"
              className={`form-control ${isNew ? 'border-success' : ''}`}
              value={currentValue}
              onChange={(e) => handleParameterChange(featureKey, param.key, parseInt(e.target.value) || 0)}
              min={param.min}
              step="1"
            />
            {isNew && (
              <div className="position-absolute top-0 end-0 mt-1 me-2">
                <i className="bi bi-star-fill text-success" title="This parameter is new and requires to be configured"></i>
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className="position-relative">
            <input
              type="text"
              className={`form-control ${isNew ? 'border-success' : ''}`}
              value={currentValue}
              onChange={(e) => handleParameterChange(featureKey, param.key, e.target.value)}
            />
            {isNew && (
              <div className="position-absolute top-0 end-0 mt-1 me-2">
                <i className="bi bi-star-fill text-success" title="This param is new and requires to be configured"></i>
              </div>
            )}
          </div>
        );
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={configBreadcrumbs}>Update Features</PageTitle>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        onConfirm={handleConfirmSave}
        title={`Confirm Changes for ${selectedAppName} - All Platforms`}
        changes={getChanges()}
        loading={isConfirming}
      />

      <div className="d-flex h-100 features-container" style={{ minHeight: 'calc(95vh - 200px)' }}>
        {/* Left Panel - Apps List */}
        <AppList
          apps={apps}
          selectedApp={selectedAppName}
          isLoadingApps={isLoadingApps}
          searchTerm={searchTerm}
          onAppSelection={onAppSelection}
          onSearchChange={setSearchTerm}
          onClearSearch={() => setSearchTerm('')}
        />
        {/* Right Panel - Configuration Editor */}
        {selectedAppName.length > 0 ? (
          <div className="w-75 config-panel">
            <div className="p-4">
              {isLoadingAppData ? (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mb-0">Loading configuration...</p>
                </div>
              ) : (
                <>
                  {/* Platform Tabs */}
                  <div className="platform-tabs mb-4">
                    <h6 className="text-muted mb-3">Select Platform:</h6>
                    <div className="d-flex gap-2 flex-wrap">
                      {platforms?.map((platform) => (
                        <div key={platform} className="position-relative">
                          <button
                            className={`platform-tab btn px-4 py-2 ${selectedPlatform === platform
                              ? 'platform-tab-active'
                              : 'platform-tab-inactive'
                              }`}
                            onClick={() => onPlatformSelection(platform)}
                            style={{ textTransform: 'capitalize' }}
                          >
                            {platform}
                          </button>
                          {canDeletePlatform(platform) && (
                            <button
                              className="btn btn-sm btn-outline-danger position-absolute top-0 end-0"
                              style={{
                                transform: 'translate(50%, -50%)',
                                width: '20px',
                                height: '20px',
                                padding: '0',
                                fontSize: '10px',
                                borderRadius: '50%'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                deletePlatform(platform);
                              }}
                              title={`Delete platform "${platform}"`}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Configuration Editor */}
                  <div className="config-form-container">
                    <div className="config-form-header mb-4">
                      <h5 className="fw-bold text-dark mb-2">Feature Configuration</h5>
                      <p className="text-muted mb-0">Configure detection features for {selectedPlatform} platform</p>
                    </div>

                    <div className="config-form-wrapper">
                      {appConfig && Object.keys(appConfig?.[selectedPlatform] || {}).length > 0 ? (
                        <div className="features-grid">
                          {Object.entries(appConfig?.[selectedPlatform] || {}).map(([featureKey, feature]: [string, any]) => {
                            // Skip rendering if feature was deleted
                            if (currentPlatformDeletedItems[featureKey] === 'DELETED_FEATURE') {
                              return null;
                            }

                            const isNew = isNewFeature(featureKey);
                            const hasNew = hasNewParameters(featureKey);
                            return (
                              <div key={featureKey} className={`feature-card card mb-4 ${isNew ? 'border-success border-2' : ''}`}>
                                <div className={`card-header d-flex align-items-center justify-content-between ${isNew ? 'bg-success bg-opacity-10' : ''}`}>
                                  <div className="d-flex align-items-center">
                                    <div className="form-check me-3">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`feature-${featureKey}`}
                                        checked={formData[featureKey]?.enable || false}
                                        onChange={(e) => onFeatureEnabledDisabled(featureKey, e.target.checked)}
                                      />
                                      <label className="form-check-label fw-bold" htmlFor={`feature-${featureKey}`}>
                                        {prettifyFeatureKey(featureKey)} {formData[featureKey]?.enable ? '' : '- Disabled'}
                                      </label>
                                    </div>
                                    {isNew && (
                                      <span className="badge bg-success ms-2">
                                        <i className="bi bi-star-fill me-1"></i>
                                        New
                                      </span>
                                    )}
                                    {hasNew && !isNew && (
                                      <span className="badge bg-info text-white ms-2">
                                        <i className="bi bi-star-fill me-1"></i>
                                        New Parameters
                                      </span>
                                    )}
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => deleteFeature(featureKey)}
                                      title={`Delete feature "${featureKey}"`}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                    <button
                                      className="btn btn-link p-0 text-muted"
                                      onClick={() => toggleCardCollapse(featureKey)}
                                      title={collapsedCards.has(featureKey) ? "Expand" : "Collapse"}
                                    >
                                      <i className={`bi bi-chevron-${collapsedCards.has(featureKey) ? 'right' : 'down'} fs-5`}></i>
                                    </button>
                                  </div>
                                </div>
                                {!collapsedCards.has(featureKey) && (
                                  <>
                                    {feature?.params?.length > 0 && (
                                      <div className="card-body">
                                        <div className="row">
                                          {feature.params
                                            .filter((param: any) => {
                                              // Filter out deleted parameters
                                              const deletedParams = currentPlatformDeletedItems[featureKey];
                                              if (deletedParams === 'DELETED_FEATURE' || !Array.isArray(deletedParams)) {
                                                return true; // Show all parameters if feature not deleted
                                              }
                                              return !deletedParams.includes(param.key);
                                            })
                                            .map((param: any) => (
                                              <div key={param.key} className="col-md-6 mb-3">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                  <label className="form-label fw-semibold d-flex align-items-center mb-0">
                                                    {param.key}
                                                    {isNewParameter(featureKey, param.key) && (
                                                      <span className="badge bg-success text-white ms-2" title="This param is new and requires to be configured">
                                                        <i className="bi bi-star-fill me-1"></i>
                                                        New
                                                      </span>
                                                    )}
                                                  </label>
                                                  <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => deleteParameter(featureKey, param.key)}
                                                    title={`Delete parameter "${param.key}"`}
                                                  >
                                                    <i className="bi bi-trash"></i>
                                                  </button>
                                                </div>
                                                {renderParameterInput(featureKey, param)}
                                                {isNewParameter(featureKey, param.key) && (
                                                  <small className="text-success">
                                                    <i className="bi bi-exclamation-circle-fill me-1"></i>
                                                    This param is new and requires to be configured
                                                  </small>
                                                )}
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                    )}
                                    {feature?.params?.length === 0 && (
                                      <div className="card-body">
                                        <p className="text-muted mb-0">No parameters required for this feature.</p>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px', width: '100%' }}>
                          <div className="text-center text-muted">
                            <i className="bi bi-arrow-left-circle fs-1 mb-3"></i>
                            <h5>The Selected Platform Has No Parameters To Configure</h5>
                            <p>Please select another platform from the top or add new parameters from the admin section</p>
                          </div>
                        </div>
                      )}



                      {/* Save Button and Status */}
                      {appConfig && Object.keys(appConfig?.[selectedPlatform] || {}).length > 0 && (
                        <div className="form-actions mt-4 pt-4 border-top">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="save-status">
                              {saveMessage && (
                                <div className={`alert alert-${saveMessage.type === 'success' ? 'success' : 'danger'} mb-0`}>
                                  {saveMessage.text}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="btn btn-primary btn-lg save-btn"
                              onClick={handleSave}
                            >
                              <i className="bi bi-check-circle me-2"></i>
                              Save Configuration
                            </button>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="w-75 d-flex align-items-center justify-content-center">
            <div className="text-center text-muted">
              <i className="bi bi-arrow-left-circle fs-1 mb-3"></i>
              <h5>No App Selected</h5>
              <p>Please select an app from the left panel to configure features</p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export { UpdateFeatures } 